import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Button,
  CircularProgress
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  ShowChart as ShowChartIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import axios from 'axios';
import PortfolioPerformance from '../components/analytics/PortfolioPerformance';
import TradeFilters from '../components/common/TradeFilters';
import useTradeFilters from '../hooks/useTradeFilters';

const PortfolioPage = () => {
  const [trades, setTrades] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [strategyNamesMap, setStrategyNamesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  const {
    selectedCategory, selectedSymbol, selectedStrategy, selectedStatus, selectedTags, startDate, endDate,
    setSelectedCategory, setSelectedSymbol, setSelectedStrategy, setSelectedStatus, setSelectedTags, setStartDate, setEndDate,
    filteredTrades, symbols, statuses, allTags, handleTimeFilter
  } = useTradeFilters(trades);

  useEffect(() => {
    fetchTrades();
    fetchStrategies();
  }, []);

  const fetchTrades = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/getTrades', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrades(response.data);
    } catch (error) {
      console.error('Error fetching trades:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('idToken');
      const response = await axios.get('https://v7jlylb599.execute-api.eu-west-1.amazonaws.com/createTradeStrategy', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const strategyMap = {};
      for (const strat of response.data) {
        strategyMap[strat.strategyId] = strat.name;
      }
      setStrategies(response.data);
      setStrategyNamesMap(strategyMap);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceIcon /> Portfolio Analytics
      </Typography>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <TradeFilters
          selectedCategory={selectedCategory}
          selectedSymbol={selectedSymbol}
          selectedStrategy={selectedStrategy}
          selectedStatus={selectedStatus}
          selectedTags={selectedTags}
          startDate={startDate}
          endDate={endDate}
          onCategoryChange={setSelectedCategory}
          onSymbolChange={setSelectedSymbol}
          onStrategyChange={setSelectedStrategy}
          onStatusChange={setSelectedStatus}
          onTagsChange={setSelectedTags}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTimeFilter={handleTimeFilter}
          symbols={symbols}
          strategies={strategies.map(s => s.strategyId)}
          statuses={statuses}
          allTags={allTags}
          strategyNamesMap={strategyNamesMap}
        />
      </Paper>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 3 }}
      >
        <Tab icon={<TrendingUpIcon />} label="Performance" />
        <Tab icon={<AssessmentIcon />} label="Risk Analysis" />
        <Tab icon={<ShowChartIcon />} label="Equity Curve" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Performance Tab */}
          {activeTab === 0 && (
            <PortfolioPerformance trades={filteredTrades} />
          )}

          {/* Risk Analysis Tab */}
          {activeTab === 1 && (
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                Risk Analysis
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Risk Distribution
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          Risk analysis visualization will be available in the next update
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card elevation={1}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Risk Metrics
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Typography variant="body1" color="text.secondary">
                          Risk metrics will be available in the next update
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Equity Curve Tab */}
          {activeTab === 2 && (
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
                Equity Curve
              </Typography>
              
              <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  Detailed equity curve analysis will be available in the next update
                </Typography>
              </Box>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
};

export default PortfolioPage;